import { API_BASE_URL } from '../../config';
import localStorage from '../../utils/localStorage';
import { createSSEDecoder } from './sseParser';

const safeJSON = raw => {
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

/**
 * POST /v1/kai/ask and stream the SSE response. EventSource can't POST, so we
 * hand-roll fetch + ReadableStream. Handlers:
 *   onToken({ delta })
 *   onToolCall({ name, status })
 *   onUsage({ input_tokens, output_tokens, cost_usd, model, questions_remaining })
 *   onError({ code, message })   // terminal
 *   onDone({ message_id })
 *
 * Throws an error with `.status` / `.body` set before the stream starts on a
 * non-2xx response (e.g. 429 quota_exceeded) so the caller can render the
 * quota-exhausted state.
 */
export async function askStream({
  conversationId,
  query,
  context,
  signal,
  handlers = {},
}) {
  const token = localStorage.getItem('access_token');

  const res = await fetch(`${API_BASE_URL}/v1/kai/ask`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      conversation_id: conversationId,
      query,
      context,
    }),
    signal,
  });

  if (!res.ok || !res.body) {
    let body = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    const err = new Error('kai_ask_failed');
    err.status = res.status;
    err.body = body;
    throw err;
  }

  const dispatch = ({ event, data }) => {
    const payload = data ? safeJSON(data) : {};
    switch (event) {
      case 'token':
        handlers.onToken?.(payload);
        break;
      case 'tool_call':
        handlers.onToolCall?.(payload);
        break;
      case 'usage':
        handlers.onUsage?.(payload);
        break;
      case 'error':
        handlers.onError?.(payload);
        break;
      case 'done':
        handlers.onDone?.(payload);
        break;
      default:
        break;
    }
  };

  const decoder = createSSEDecoder(dispatch);
  const reader = res.body.getReader();
  const textDecoder = new TextDecoder();

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;
    decoder.push(textDecoder.decode(value, { stream: true }));
  }
  decoder.push(textDecoder.decode());
  decoder.flush();
}
